import type {
  DailyCapacity,
  DaypartT,
  EnergyT,
  ImportanceT,
  Initiative,
  Objective,
  ProtectedBlock,
  Task,
} from '@portfolio-pm/shared';

export const PRIORITIZATION_EXPLANATION_VERSION = 1 as const;

export type EligibilityClass =
  | 'eligible'
  | 'provisional'
  | 'waiting'
  | 'parked'
  | 'someday'
  | 'needs_clarification'
  | 'blocked'
  | 'future'
  | 'oversized'
  | 'done';

export type PriorityTier =
  | 'cadence_recovery'
  | 'time_critical'
  | 'strategic_progress'
  | 'active_commitment';

export type PrioritizationReasonCode =
  | 'missing_commitment_state'
  | 'missing_actionability'
  | 'missing_expected_outcome'
  | 'missing_estimate'
  | 'missing_waiting_trigger'
  | 'missing_objective_or_initiative_link'
  | 'missing_dependency'
  | 'unfinished_dependency'
  | 'future_schedule'
  | 'estimate_exceeds_capacity'
  | 'oversized_needs_decomposition'
  | 'waiting'
  | 'parked'
  | 'someday'
  | 'needs_clarification'
  | 'completed'
  | 'trashed'
  | 'missing_outcome'
  | 'missing_success_definition'
  | 'missing_importance'
  | 'missing_progress_cadence'
  | 'missing_review_horizon'
  | 'stalled_cadence'
  | 'unknown_movement';

export interface PrioritizationContext {
  version: 1;
  now: number;
  localDate: string;
  timezone: string;
  currentEnergy?: EnergyT;
  currentDaypart?: DaypartT;
  capacity?: DailyCapacity;
  protectedBlocks?: ProtectedBlock[];
}

export interface StewardshipSignal {
  entityType: 'objective' | 'initiative';
  entityId: string;
  title: string;
  importance?: ImportanceT;
  commitmentState?: Objective['commitmentState'];
  cadenceDays?: number;
  lastMovementAt?: number;
  missedCadenceIntervals: number;
  stalled: boolean;
  reasonCodes: PrioritizationReasonCode[];
  completeness: Completeness;
}

export interface Completeness {
  complete: boolean;
  known: number;
  total: number;
  ratio: number;
  reasonCodes: PrioritizationReasonCode[];
}

export interface Contribution {
  code:
    | 'overdue'
    | 'due_today'
    | 'due_soon'
    | 'scheduled_ready'
    | 'priority'
    | 'importance'
    | 'missed_cadence'
    | 'capacity_fit'
    | 'energy_match'
    | 'energy_mismatch'
    | 'daypart_match'
    | 'daypart_mismatch'
    | 'short_estimate';
  points: number;
  detail: string;
}

export interface TaskPrioritizationInput {
  task: Task;
  tasksById: ReadonlyMap<string, Task>;
  objective?: Objective;
  initiative?: Initiative;
  stewardship?: StewardshipSignal[];
}

export interface TaskRecommendation {
  task: Task;
  eligibility: EligibilityClass;
  tier?: PriorityTier;
  tierOrder?: number;
  score: number;
  completeness: Completeness;
  reasonCodes: PrioritizationReasonCode[];
  contributions: Contribution[];
  inputs: Record<string, unknown>;
  explanationVersion: typeof PRIORITIZATION_EXPLANATION_VERSION;
}

const DAY = 86_400_000;
const TIER_ORDER: Record<PriorityTier, number> = {
  cadence_recovery: 1,
  time_critical: 2,
  strategic_progress: 3,
  active_commitment: 4,
};

export function deriveDaypart(now: number): DaypartT {
  const hour = new Date(now).getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

function completeness(reasonCodes: PrioritizationReasonCode[], total: number): Completeness {
  const missing = unique(reasonCodes);
  const known = Math.max(0, total - missing.length);
  return {
    complete: missing.length === 0,
    known,
    total,
    ratio: known / total,
    reasonCodes: missing,
  };
}

function localDate(timestamp: number, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(timestamp);
  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function importancePoints(value: ImportanceT | undefined): number {
  return value === 'protected' ? 10 : value === 'important' ? 6 : 0;
}

function linkedImportance(input: TaskPrioritizationInput): ImportanceT | undefined {
  const values = [input.objective?.importance, input.initiative?.importance];
  if (values.includes('protected')) return 'protected';
  if (values.includes('important')) return 'important';
  return values.includes('standard') ? 'standard' : undefined;
}

function protectedMinutes(input: TaskPrioritizationInput, context: PrioritizationContext): number {
  return (context.protectedBlocks ?? [])
    .filter(
      (block) =>
        block.localDate === context.localDate &&
        ((input.objective && block.objectiveId === input.objective.id) ||
          (input.initiative && block.initiativeId === input.initiative.id)),
    )
    .reduce((sum, block) => sum + block.durationMinutes, 0);
}

export function taskCompleteness(input: TaskPrioritizationInput): Completeness {
  const reasons: PrioritizationReasonCode[] = [];
  if (input.task.commitmentState === undefined) reasons.push('missing_commitment_state');
  if (input.task.actionability === undefined) reasons.push('missing_actionability');
  if (!input.task.expectedOutcome?.trim()) reasons.push('missing_expected_outcome');
  if (input.task.estimateMinutes === undefined) reasons.push('missing_estimate');
  if (!input.task.objectiveId && !input.task.initiativeId && !input.task.krId) {
    reasons.push('missing_objective_or_initiative_link');
  }
  if (input.task.commitmentState === 'waiting' && !input.task.availabilityTrigger?.trim()) {
    reasons.push('missing_waiting_trigger');
  }
  return completeness(reasons, 5 + (input.task.commitmentState === 'waiting' ? 1 : 0));
}

export function objectiveCompleteness(objective: Objective): Completeness {
  const reasons: PrioritizationReasonCode[] = [];
  if (!objective.outcome?.trim()) reasons.push('missing_outcome');
  if (objective.importance === undefined) reasons.push('missing_importance');
  if (objective.minimumProgressCadenceDays === undefined) reasons.push('missing_progress_cadence');
  if (objective.endDate === undefined && objective.reviewAt === undefined) {
    reasons.push('missing_review_horizon');
  }
  return completeness(reasons, 4);
}

export function initiativeCompleteness(initiative: Initiative): Completeness {
  const reasons: PrioritizationReasonCode[] = [];
  if (!initiative.successDefinition?.trim()) reasons.push('missing_success_definition');
  if (initiative.importance === undefined) reasons.push('missing_importance');
  if (initiative.minimumProgressCadenceDays === undefined) reasons.push('missing_progress_cadence');
  if (initiative.reviewAt === undefined && !initiative.reviewTrigger?.trim()) {
    reasons.push('missing_review_horizon');
  }
  return completeness(reasons, 4);
}

export function stewardshipSignal(
  entity: Objective | Initiative,
  entityType: 'objective' | 'initiative',
  lastMovementAt: number | undefined,
  now: number,
): StewardshipSignal {
  const cadenceDays = entity.minimumProgressCadenceDays;
  const missedCadenceIntervals =
    cadenceDays && lastMovementAt !== undefined
      ? Math.max(0, Math.floor((now - lastMovementAt) / (cadenceDays * DAY)))
      : 0;
  const stalled =
    entity.commitmentState === 'active_now' &&
    entity.importance !== undefined &&
    entity.importance !== 'standard' &&
    missedCadenceIntervals > 0;
  const complete =
    entityType === 'objective'
      ? objectiveCompleteness(entity as Objective)
      : initiativeCompleteness(entity as Initiative);
  const movementUnknown =
    lastMovementAt === undefined &&
    entity.commitmentState === 'active_now' &&
    entity.importance !== undefined &&
    entity.importance !== 'standard' &&
    cadenceDays !== undefined;
  const reasonCodes = unique<PrioritizationReasonCode>([
    ...complete.reasonCodes,
    ...(stalled ? (['stalled_cadence'] as const) : []),
    ...(movementUnknown ? (['unknown_movement'] as const) : []),
  ]);
  return {
    entityType,
    entityId: entity.id,
    title: entity.title,
    ...(entity.importance === undefined ? {} : { importance: entity.importance }),
    ...(entity.commitmentState === undefined ? {} : { commitmentState: entity.commitmentState }),
    ...(cadenceDays === undefined ? {} : { cadenceDays }),
    ...(lastMovementAt === undefined ? {} : { lastMovementAt }),
    missedCadenceIntervals,
    stalled,
    reasonCodes,
    completeness: complete,
  };
}

export function evaluateTask(
  input: TaskPrioritizationInput,
  context: PrioritizationContext,
): TaskRecommendation {
  const { task } = input;
  const comp = taskCompleteness(input);
  const reasons: PrioritizationReasonCode[] = [];
  let eligibility: EligibilityClass = 'eligible';

  if (task.completedAt !== undefined) {
    eligibility = 'done';
    reasons.push('completed');
  } else if (task.trashedAt !== undefined) {
    eligibility = 'done';
    reasons.push('trashed');
  } else if (task.commitmentState === 'waiting') {
    eligibility = 'waiting';
    reasons.push('waiting');
    if (!task.availabilityTrigger?.trim()) reasons.push('missing_waiting_trigger');
  } else if (task.commitmentState === 'later_parked') {
    eligibility = 'parked';
    reasons.push('parked');
  } else if (task.commitmentState === 'someday') {
    eligibility = 'someday';
    reasons.push('someday');
  } else if (task.commitmentState === 'needs_clarification') {
    eligibility = 'needs_clarification';
    reasons.push('needs_clarification');
  } else if (
    task.commitmentState === undefined ||
    task.actionability === undefined ||
    task.estimateMinutes === undefined
  ) {
    eligibility = 'provisional';
  } else if (task.scheduledAt !== undefined && task.scheduledAt > context.now) {
    eligibility = 'future';
    reasons.push('future_schedule');
  } else {
    for (const dependencyId of task.dependsOn ?? []) {
      const dependency = input.tasksById.get(dependencyId);
      if (!dependency) reasons.push('missing_dependency');
      else if (dependency.completedAt === undefined) reasons.push('unfinished_dependency');
    }
    if (reasons.some((code) => code === 'missing_dependency' || code === 'unfinished_dependency')) {
      eligibility = 'blocked';
    } else if (task.actionability !== 'concrete') {
      eligibility = task.actionability === 'needs_decomposition' ? 'oversized' : 'provisional';
      if (task.actionability === 'needs_decomposition')
        reasons.push('oversized_needs_decomposition');
    } else {
      const blockMinutes = protectedMinutes(input, context);
      const available = Math.max(context.capacity?.availableMinutes ?? 0, blockMinutes);
      if (task.estimateMinutes >= 240 && blockMinutes < task.estimateMinutes) {
        eligibility = 'oversized';
        reasons.push('oversized_needs_decomposition');
      } else if (context.capacity && task.estimateMinutes > available) {
        eligibility = 'provisional';
        reasons.push('estimate_exceeds_capacity');
      }
    }
  }

  const contributions: Contribution[] = [];
  const add = (code: Contribution['code'], points: number, detail: string): void => {
    if (points !== 0) contributions.push({ code, points, detail });
  };
  const dueDate = task.dueAt === undefined ? undefined : localDate(task.dueAt, context.timezone);
  if (task.dueAt !== undefined && task.dueAt < context.now) {
    const days = Math.floor((context.now - task.dueAt) / DAY);
    add('overdue', Math.min(30, 15 + 2 * days), `${days} full day(s) overdue`);
  } else if (dueDate === context.localDate) {
    add('due_today', 12, 'Due today');
  } else if (task.dueAt !== undefined) {
    const days = Math.ceil((task.dueAt - context.now) / DAY);
    if (days > 0 && days <= 7) add('due_soon', Math.max(1, 8 - days), `Due in ${days} day(s)`);
  }
  if (task.scheduledAt !== undefined && task.scheduledAt <= context.now) {
    add('scheduled_ready', 8, 'Scheduled time has arrived');
  }
  add('priority', task.priority * 3, `Task priority P${task.priority}`);
  const importance = linkedImportance(input);
  add(
    'importance',
    importancePoints(importance),
    `Linked importance is ${importance ?? 'unknown'}`,
  );
  const missed = Math.max(
    0,
    ...(input.stewardship ?? []).map((signal) => signal.missedCadenceIntervals),
  );
  add('missed_cadence', Math.min(15, 5 * missed), `${missed} cadence interval(s) missed`);
  if (context.capacity && task.estimateMinutes !== undefined) {
    if (
      task.estimateMinutes <=
      Math.max(context.capacity.availableMinutes, protectedMinutes(input, context))
    ) {
      add('capacity_fit', 6, 'Fits known capacity');
    }
  }
  if (task.energy && context.currentEnergy) {
    add(
      task.energy === context.currentEnergy ? 'energy_match' : 'energy_mismatch',
      task.energy === context.currentEnergy ? 4 : -4,
      `Task needs ${task.energy} energy`,
    );
  }
  if (task.daypart && context.currentDaypart) {
    add(
      task.daypart === context.currentDaypart ? 'daypart_match' : 'daypart_mismatch',
      task.daypart === context.currentDaypart ? 3 : -3,
      `Task fits ${task.daypart}`,
    );
  }
  if (task.estimateMinutes !== undefined && task.estimateMinutes <= 90) {
    add(
      'short_estimate',
      task.estimateMinutes <= 30 ? 2 : 1,
      `${task.estimateMinutes} minute estimate`,
    );
  }

  const hasStalled = (input.stewardship ?? []).some((signal) => signal.stalled);
  const tier: PriorityTier = hasStalled
    ? 'cadence_recovery'
    : task.dueAt !== undefined || task.scheduledAt !== undefined
      ? 'time_critical'
      : importance === 'important' || importance === 'protected'
        ? 'strategic_progress'
        : 'active_commitment';
  const score = contributions.reduce((sum, contribution) => sum + contribution.points, 0);
  return {
    task,
    eligibility,
    ...(eligibility === 'eligible' || eligibility === 'provisional'
      ? { tier, tierOrder: TIER_ORDER[tier] }
      : {}),
    score,
    completeness: comp,
    reasonCodes: unique([...reasons, ...comp.reasonCodes]),
    contributions,
    inputs: {
      now: context.now,
      localDate: context.localDate,
      timezone: context.timezone,
      capacityMinutes: context.capacity?.availableMinutes ?? null,
      taskUpdatedAt: task.updatedAt,
      commitmentState: task.commitmentState ?? null,
      actionability: task.actionability ?? null,
      estimateMinutes: task.estimateMinutes ?? null,
      dependencyIds: task.dependsOn ?? [],
      objectiveId: task.objectiveId ?? null,
      initiativeId: task.initiativeId ?? null,
      importance: importance ?? null,
      missedCadenceIntervals: missed,
    },
    explanationVersion: PRIORITIZATION_EXPLANATION_VERSION,
  };
}

function timeOrInfinity(value: number | undefined): number {
  return value ?? Number.POSITIVE_INFINITY;
}

export function rankRecommendations(recommendations: TaskRecommendation[]): TaskRecommendation[] {
  return [...recommendations].sort((a, b) => {
    if ((a.tierOrder ?? 99) !== (b.tierOrder ?? 99))
      return (a.tierOrder ?? 99) - (b.tierOrder ?? 99);
    if (a.score !== b.score) return b.score - a.score;
    if (timeOrInfinity(a.task.dueAt) !== timeOrInfinity(b.task.dueAt)) {
      return timeOrInfinity(a.task.dueAt) - timeOrInfinity(b.task.dueAt);
    }
    if (timeOrInfinity(a.task.scheduledAt) !== timeOrInfinity(b.task.scheduledAt)) {
      return timeOrInfinity(a.task.scheduledAt) - timeOrInfinity(b.task.scheduledAt);
    }
    if (a.task.createdAt !== b.task.createdAt) return a.task.createdAt - b.task.createdAt;
    const title = a.task.title.localeCompare(b.task.title);
    return title || a.task.id.localeCompare(b.task.id);
  });
}

export function rankPrioritization(
  inputs: TaskPrioritizationInput[],
  context: PrioritizationContext,
): TaskRecommendation[] {
  return rankRecommendations(inputs.map((input) => evaluateTask(input, context)));
}
